import React from "react";
import { useParams, Link } from "react-router-dom";
import { usePlayer } from "../apiCalls/userApiCalls";
import {
  Container,
  Card,
  Badge,
  Button,
  CountryLabel,
  RankMovement,
  VerifiedTick,
} from "../components/ui";
import {
  ArrowLeftIcon,
  BuildingIcon,
  UserIcon,
  PinIcon,
  CalendarIcon,
  TrophyIcon,
} from "../components/ui/icons";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";
import normalDate, { formatNumber, getAge } from "../utils";

// Each fact appears exactly once on this page. Identity (name, country, club,
// bio) lives in the header; the numbers live in the header's right rail; and
// everything else is listed in the details table below. Tournaments played,
// titles, win/loss and points expiry need the P4/P7 models and are absent
// rather than shown empty.
const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border py-3 last:border-0">
    <span className="flex items-center gap-2 text-sm text-muted">
      {icon}
      {label}
    </span>
    <span className="text-right text-sm font-medium text-navy">{value}</span>
  </div>
);

export default function PlayerDetails() {
  const { idOrSlug } = useParams();
  const { isLoading, data: player, isError } = usePlayer(idOrSlug);

  if (isLoading) return <Loader label="Loading player…" />;
  if (isError || !player)
    return <ErrorState message="We couldn't find that player." />;

  const age = getAge(player.dateOfBirth);
  const moved =
    player.rank && player.previousRank && player.previousRank !== player.rank;

  return (
    <Container className="py-8 md:py-10">
      <Link
        to="/rankings"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Rankings
      </Link>

      <Card padded={false} className="overflow-hidden">
        <div className="grid lg:grid-cols-[18rem_1fr_16rem]">
          <figure className="aspect-square bg-bg lg:aspect-auto lg:h-full">
            <img
              className="h-full w-full object-cover"
              src={
                player.imgUrl ||
                `${process.env.PUBLIC_URL}/default-profile-image.jpg`
              }
              alt={player.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL}/default-profile-image.jpg`;
              }}
            />
          </figure>

          <div className="min-w-0 p-6 md:p-8">
            <CountryLabel code={player.nationality} className="text-muted" />

            <h1 className="mt-2 flex flex-wrap items-center gap-2.5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
              {player.name}
              {player.isClaimed && <VerifiedTick className="h-6 w-6" />}
            </h1>

            {player.club && (
              <Link
                to="/clubs"
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-primary"
              >
                <BuildingIcon className="h-4 w-4 text-muted" />
                {player.club.name}
                {player.club.city && (
                  <span className="font-normal text-muted">
                    · {player.club.city}
                  </span>
                )}
              </Link>
            )}

            {player.description && (
              <p className="mt-4 max-w-xl leading-relaxed text-muted">
                {player.description}
              </p>
            )}

            {!player.isClaimed && (
              <div className="mt-5">
                <Badge tone="neutral">Profile not yet claimed</Badge>
              </div>
            )}
          </div>

          {/* The numbers. Only place on the page they appear. */}
          <div className="border-t border-border bg-bg p-6 md:p-8 lg:border-l lg:border-t-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">
              World Rank
            </div>
            <div className="tabular mt-1 text-5xl font-bold leading-none text-primary">
              {player.rank ? `#${player.rank}` : "—"}
            </div>
            <div className="mt-1.5 text-xs text-muted">
              Among {player.gender === "Female" ? "women" : "men"}
            </div>

            <div className="mt-7 text-xs font-semibold uppercase tracking-wide text-muted">
              Ranking Points
            </div>
            <div className="tabular mt-1 text-3xl font-bold leading-none text-navy">
              {formatNumber(player.rankingPoints)}
            </div>

            {moved && (
              <div className="mt-6 inline-flex items-center rounded-full border border-border bg-surface px-3 py-1.5">
                <RankMovement
                  rank={player.rank}
                  previousRank={player.previousRank}
                  showLabel
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1fr]">
        <Card>
          <h2 className="mb-2 font-semibold text-navy">Player details</h2>
          <DetailRow
            icon={<UserIcon className="h-4 w-4" />}
            label="Gender"
            value={player.gender}
          />
          {age && (
            <DetailRow
              icon={<CalendarIcon className="h-4 w-4" />}
              label="Age"
              value={age}
            />
          )}
          <DetailRow
            icon={<PinIcon className="h-4 w-4" />}
            label="Country"
            value={<CountryLabel code={player.nationality} />}
          />
          <DetailRow
            icon={<BuildingIcon className="h-4 w-4" />}
            label="Club"
            value={player.club?.name || "Independent"}
          />
          <DetailRow
            icon={<TrophyIcon className="h-4 w-4" />}
            label="Status"
            value={
              player.status === "active" ? "Active on tour" : "Not yet active"
            }
          />
          <DetailRow
            icon={<CalendarIcon className="h-4 w-4" />}
            label="Joined PadelRank"
            value={normalDate(player.createdAt)}
          />
        </Card>

        {/* Most players are entered from a tournament sheet and never sign up,
            so an unclaimed profile is normal — and an invitation. */}
        {!player.isClaimed ? (
          <Card className="flex flex-col justify-center bg-primary-wash">
            <h2 className="font-semibold text-navy">Is this you?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              This profile was added from tournament records and hasn't been
              claimed yet. Create an account to claim it and keep your details
              up to date.
            </p>
            <div className="mt-5">
              <Button to="/register">Claim this profile</Button>
            </div>
          </Card>
        ) : (
          <Card className="flex flex-col justify-center">
            <h2 className="font-semibold text-navy">Ranking history</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Match results and a full points history arrive with the scoring
              engine. For now, this position is derived from current ranking
              points alone.
            </p>
            <div className="mt-5">
              <Button to="/how-rankings-work" variant="secondary">
                How rankings work
              </Button>
            </div>
          </Card>
        )}
      </div>
    </Container>
  );
}
